import { ref, query, equalTo, orderByChild, getDatabase, get, onValue, forceLongPolling } from "firebase/database";
import { useEffect, useMemo, useRef, useState } from "react";

// After the first answer, the changes that come from the server are shown at most this often, so
// a bulk upload (many records changing one after another) does not redraw the page for each one
const LIVE_DELAY_MS = 700

// Reads the records of a query. Returns [items, loading, error, refresh].
// - `live` false (default): ONE read, redone only by `refresh`. For lists that change all the time
//   without the user doing anything (locations and devices: the players write a ping into them),
//   where a live connection would only stream pings.
// - `live` true: the list follows the database, so what another user adds (content, broadcasts)
//   shows up without reloading the page. `refresh` then only shows a pending change at once.
export const useDbQuery = (query, { live = false } = {}) => {

  const cache = useRef({})
  const flushLive = useRef(null)

  const [state, setState] = useState({
    snapshots: null, error: null
  })

  const [loading, setLoading] = useState(true)


  const getVals = async (force) => {
    try {
      setLoading(true)

      const key = query.toString()
      if (force || !cache.current[key]) {
        const snapshots = await get(query)
        const vals = []
        snapshots.forEach((val) => {
          vals.push(val.val())
        })
        
        cache.current[key] = vals
        setState({ snapshots: vals, error: null })
      } else {
        setState({ snapshots: cache.current[key], error: null })
      }
      // console.log(cache)
      setLoading(false)
    } catch (err) {
      console.trace(err)
      setState({ snapshots: [], loading: false, error: err.message })
      setLoading(false)
    }
  }

  useEffect(() => {
    if (query && !state.snapshots) {
      // getVals(false)
    }
  }, [query, state.snapshots])

  // Live list: one listener per query (a new one when the query changes, e.g. the client id
  // arrives). The first answer is shown at once, later changes are collected and shown together.
  // The query is rebuilt on every render of the caller, so the effect must restart only when it is
  // really another query. NOT `query.toString()`: that is only the address of the node and leaves
  // out the filter, so "clientId = loading" and "clientId = <the real one>" looked the same, the
  // listener was never made again and the list stayed empty. `isEqual` compares the filter too.
  const queryRef = useRef(query)
  if (!queryRef.current.isEqual(query)) queryRef.current = query
  const stableQuery = queryRef.current
  useEffect(() => {
    if (!live) return undefined

    let active = true
    let first = true
    let latest = null
    let timer = null

    const publish = () => {
      clearTimeout(timer)
      timer = null
      if (!active || latest === null) return
      const vals = latest
      latest = null
      setState({ snapshots: vals, error: null })
      setLoading(false)
    }
    flushLive.current = publish

    setLoading(true)
    const unsubscribe = onValue(stableQuery, (snapshot) => {
      const vals = []
      snapshot.forEach((child) => { vals.push(child.val()) })
      latest = vals
      if (first) {
        first = false
        publish()
      } else if (!timer) {
        timer = setTimeout(publish, LIVE_DELAY_MS)
      }
    }, (err) => {
      if (!active) return
      setState({ snapshots: [], error: err.message })
      setLoading(false)
    })

    return () => {
      active = false
      clearTimeout(timer)
      flushLive.current = null
      unsubscribe()
    }
  }, [live, stableQuery])

  return [
    state.snapshots || [],
    loading,
    state.error, 
    live
      ? () => { flushLive.current && flushLive.current(); return Promise.resolve() }
      : () => getVals(true)
  ]
};
